import type { AuthClient } from "./client";
import { FirebaseAuthClient } from "./firebase";
import { readFirebaseConfig } from "./firebase-config";
import { MockAuthClient } from "./mock";

export type { AuthClient, AuthSession, AuthUser } from "./client";
export { AuthError } from "./client";

/**
 * Thrown when a production build has no Firebase web config. Surfaced as
 * a fatal bootstrap error in main.ts rather than allowed to degrade
 * silently — see createAuthClient().
 */
export class AuthConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthConfigError";
  }
}

/**
 * Selects the auth client implementation based on whether Firebase web
 * config is available in the environment. Returns a real
 * `FirebaseAuthClient` when `VITE_FIREBASE_API_KEY` is set (production
 * on Cloudflare Pages), or the dev-only `MockAuthClient` otherwise
 * (local dev with no .env, and Vitest runs where Firebase env vars are
 * pinned to empty strings via `vite.config.ts` → `test.env`).
 *
 * Hardening: in a PRODUCTION build a missing config is never acceptable —
 * it means the deploy shipped with an empty `VITE_FIREBASE_API_KEY`, and
 * silently falling back to `MockAuthClient` would issue `dev-token-*`
 * tokens the API rejects with a 401, presenting as broken auth with no
 * obvious cause. Fail loudly instead so a misconfigured deploy is caught
 * immediately (and reported to Sentry) rather than shipping fake auth.
 * The mock is only ever used in non-production builds (local dev and
 * Vitest, where `import.meta.env.PROD` is false).
 */
export function createAuthClient(): AuthClient {
  const config = readFirebaseConfig();
  if (config) return new FirebaseAuthClient(config);
  if (import.meta.env.PROD) {
    throw new AuthConfigError(
      "Firebase web config is missing in a production build " +
        "(VITE_FIREBASE_API_KEY is empty). Refusing to fall back to " +
        "MockAuthClient, which would issue tokens the API rejects (401). " +
        "Set the firebase_* Terraform vars and redeploy the frontend.",
    );
  }
  return new MockAuthClient();
}
